import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Stack,
  Chip
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Language as WebIcon,
  CameraAlt as CameraAltIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ProfileStats from './ProfileStats';
import axios from '../../api/axiosConfig';

const displayUrl = (url) => {
  try {
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  } catch {
    return url;
  }
};

const safeHref = (url) => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol) ? url : '#';
  } catch {
    return '#';
  }
};

const formatJoinDate = (date) =>
  new Date(date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

const ProfileHeader = ({ profile, stats, currentUser }) => {
  const theme = useTheme();
  const [isFollowing, setIsFollowing] = useState(profile.isFollowing || false);
  const [followLoading, setFollowLoading] = useState(false);
  const [localFollowerCount, setLocalFollowerCount] = useState(profile.followerCount || 0);
  const [hoveringFollowBtn, setHoveringFollowBtn] = useState(false);

  const handleToggleFollow = async () => {
    if (followLoading) return;
    setFollowLoading(true);

    const wasFollowing = isFollowing;
    // Optimistic update
    setIsFollowing(!wasFollowing);
    setLocalFollowerCount(prev => wasFollowing ? prev - 1 : prev + 1);

    try {
      if (wasFollowing) {
        await axios.delete(`/api/u/${profile.username}/follow`);
      } else {
        await axios.post(`/api/u/${profile.username}/follow`);
      }
    } catch (err) {
      // Revert on failure
      console.error('Follow toggle failed:', err);
      setIsFollowing(wasFollowing);
      setLocalFollowerCount(prev => wasFollowing ? prev + 1 : prev - 1);
    } finally {
      setFollowLoading(false);
    }
  };

  const hasDisplayName =
    profile.displayName &&
    profile.displayName.toLowerCase() !== profile.username.toLowerCase();

  const isOwner = currentUser && currentUser.uid === profile.firebaseUid;

  return (
    <Card sx={{ mb: 3, overflow: 'hidden' }}>
      {/* Cover banner — custom image or gradient fallback */}
      <Box
        sx={{
          height: { xs: 140, sm: 160, md: 180 },
          position: 'relative',
          ...(profile.coverImageUrl
            ? {
                backgroundImage: `url(${profile.coverImageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }
            : {
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.dark} 100%)`
              }
          ),
          '&:hover .cover-camera': { opacity: 1 }
        }}
      >
        {/* Owner-only camera overlay — links to Settings */}
        {isOwner && (
          <Box
            className="cover-camera"
            component={Link}
            to="/settings"
            sx={{
              position: 'absolute', bottom: 8, right: 8,
              bgcolor: 'rgba(0,0,0,0.55)', borderRadius: '50%',
              width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: 0, transition: 'opacity 0.2s', cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
            }}
          >
            <CameraAltIcon sx={{ color: 'white', fontSize: 18 }} />
          </Box>
        )}
      </Box>

      <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center', mt: -6 }}>
        {/* Avatar overlapping the banner */}
        <Box sx={{ position: 'relative', display: 'inline-block', '&:hover .avatar-camera': { opacity: 1 } }}>
          <Avatar
            src={profile.profilePictureUrl}
            alt={`@${profile.username}`}
            sx={{
              width: 96,
              height: 96,
              mx: 'auto',
              mb: 2,
              fontSize: '2.2rem',
              bgcolor: 'primary.main',
              border: '4px solid',
              borderColor: 'background.paper',
              boxShadow: 3
            }}
          >
            {profile.username?.[0]?.toUpperCase()}
          </Avatar>
          {/* Owner-only avatar camera overlay */}
          {isOwner && (
            <Box
              className="avatar-camera"
              component={Link}
              to="/settings"
              sx={{
                position: 'absolute', bottom: 10, right: -4,
                bgcolor: 'rgba(0,0,0,0.55)', borderRadius: '50%',
                width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: 0, transition: 'opacity 0.2s', cursor: 'pointer',
                textDecoration: 'none', border: '2px solid', borderColor: 'background.paper',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
              }}
            >
              <CameraAltIcon sx={{ color: 'white', fontSize: 14 }} />
            </Box>
          )}
        </Box>

        {/* Display name + username */}
        {hasDisplayName ? (
          <>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.25 }}>
              {profile.displayName}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
              @{profile.username}
            </Typography>
          </>
        ) : (
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            @{profile.username}
          </Typography>
        )}

        {/* Bio */}
        {profile.bio && (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 2,
              maxWidth: 480,
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            {profile.bio}
          </Typography>
        )}

        {/* Stats */}
        <Box sx={{ mb: 2 }}>
          <ProfileStats stats={stats} profile={{ ...profile, followerCount: localFollowerCount }} />
        </Box>

        {/* Social links & metadata */}
        <Stack
          direction="row"
          justifyContent="center"
          sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}
        >
          {profile.joinDate && (
            <Chip
              icon={<CalendarIcon sx={{ fontSize: '15px !important' }} />}
              label={`Joined ${formatJoinDate(profile.joinDate)}`}
              size="small"
              variant="outlined"
              sx={{ height: 28, fontSize: '0.78rem' }}
            />
          )}
          {profile.twitterHandle && (
            <Chip
              label={`@${profile.twitterHandle}`}
              size="small"
              variant="outlined"
              component="a"
              href={`https://x.com/${encodeURIComponent(profile.twitterHandle)}`}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              sx={{
                height: 28,
                fontSize: '0.78rem',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
              }}
            />
          )}
          {profile.websiteUrl && (
            <Chip
              icon={<WebIcon sx={{ fontSize: '15px !important' }} />}
              label={displayUrl(profile.websiteUrl)}
              size="small"
              variant="outlined"
              component="a"
              href={safeHref(profile.websiteUrl)}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              sx={{
                height: 28,
                fontSize: '0.78rem',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
              }}
            />
          )}
        </Stack>

        {/* CTA — hide on own profile */}
        {!currentUser ? (
          <Button
            variant="contained"
            component={Link}
            to="/signup"
            sx={{
              textTransform: 'none',
              borderRadius: 3,
              px: 4,
              py: 1,
              fontWeight: 600
            }}
          >
            Sign up to follow @{profile.username}
          </Button>
        ) : currentUser.uid !== profile.firebaseUid ? (
          <Button
            variant={isFollowing ? 'contained' : 'outlined'}
            onClick={handleToggleFollow}
            disabled={followLoading}
            onMouseEnter={() => setHoveringFollowBtn(true)}
            onMouseLeave={() => setHoveringFollowBtn(false)}
            sx={{
              textTransform: 'none',
              borderRadius: 3,
              px: 4,
              minWidth: 120,
              ...(isFollowing && hoveringFollowBtn && {
                bgcolor: 'error.main',
                borderColor: 'error.main',
                '&:hover': { bgcolor: 'error.dark', borderColor: 'error.dark' }
              })
            }}
          >
            {isFollowing
              ? (hoveringFollowBtn ? 'Unfollow' : 'Following')
              : `Follow @${profile.username}`}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ProfileHeader;
